/* ABRSM PDP ES module
   Handles: tabs (ARIA + keyboard), clamp toggles, social proof,
   ATC bar, hover swap, lazy embed hydration, mobile sticky ATC */

function getAssociatedTabContent(tabInput) {
  if (!tabInput) return null;

  let sibling = tabInput.nextElementSibling;
  if (sibling && sibling.classList.contains('product-tab-label')) {
    sibling = sibling.nextElementSibling;
  }

  return sibling && sibling.classList.contains('product-tab-content') ? sibling : null;
}

function hydrateIframe(iframe) {
  if (!iframe || iframe.getAttribute('data-embed-loaded') === 'true') return;

  const embedSrc = iframe.getAttribute('data-embed-src');
  if (!embedSrc) return;

  iframe.setAttribute('src', embedSrc);
  iframe.setAttribute('data-embed-loaded', 'true');
}

function hydrateVisibleEmbeds(scope) {
  if (!scope) return;

  scope.querySelectorAll('iframe[data-embed-src]:not([data-embed-loaded="true"])').forEach((frame) => {
    const tabContent = frame.closest('.product-tab-content');
    if (tabContent) {
      let tabInput = tabContent.previousElementSibling;
      if (tabInput && tabInput.classList.contains('product-tab-label')) {
        tabInput = tabInput.previousElementSibling;
      }

      if (tabInput && tabInput.classList.contains('product-tab-input') && !tabInput.checked) {
        return;
      }
    }

    hydrateIframe(frame);
  });
}

function wireClamp(root, bodySel, fadeSel, toggleSel, clampClass) {
  const body = root.querySelector(bodySel);
  const fade = root.querySelector(fadeSel);
  const toggle = root.querySelector(toggleSel);
  if (!(body && toggle)) return;

  function syncToggleText(isClamped) {
    const moreText = toggle.getAttribute('data-more-text') || 'See more';
    const lessText = toggle.getAttribute('data-less-text') || 'See less';

    toggle.textContent = isClamped ? moreText : lessText;
    toggle.setAttribute('aria-expanded', isClamped ? 'false' : 'true');

    if (fade) {
      fade.classList.toggle('pdp-desc__fade--hidden', !isClamped);
      fade.classList.toggle('pdp-contents__fade--hidden', !isClamped);
    }
  }

  syncToggleText(body.classList.contains(clampClass));

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    const isClamped = body.classList.contains(clampClass);

    if (isClamped) {
      body.classList.remove(clampClass);
      syncToggleText(false);
    } else {
      body.classList.add(clampClass);
      syncToggleText(true);
      body.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });
}

function initClampToggles(section) {
  section.querySelectorAll('[data-desc-root]').forEach((root) => {
    wireClamp(root, '[data-desc-body]', '[data-desc-fade]', '[data-desc-toggle]', 'pdp-desc--clamped');
  });

  section.querySelectorAll('[data-contents-root]').forEach((root) => {
    wireClamp(root, '[data-contents-body]', '[data-contents-fade]', '[data-contents-toggle]', 'pdp-contents--clamped');
  });
}

function setVisibility(element, isHidden) {
  if (!element) return;

  element.classList.toggle('hide', isHidden);
  element.toggleAttribute('hidden', isHidden);
  element.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
}

function isPreviewThemeContext() {
  if (window.Shopify && window.Shopify.designMode) {
    return true;
  }

  if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main') {
    return true;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    return params.has('preview_theme_id');
  } catch (error) {
    return false;
  }
}

function parseIntegerAttribute(element, attributeName, fallbackValue = 0) {
  const parsed = parseInt(element.getAttribute(attributeName) || '', 10);
  return Number.isNaN(parsed) ? fallbackValue : parsed;
}

const SOCIAL_PROOF_TRACK_URL = 'https://abrsm-shop-social-proof-api.onrender.com/api/views/track';
const SOCIAL_PROOF_TRACK_HOSTS = new Set(['shop.abrsm.org']);
const SOCIAL_PROOF_TRACK_DELAY_MS = 3000;
const SOCIAL_PROOF_TRACK_TTL_MS = 30 * 60 * 1000;
const SOCIAL_PROOF_TRACK_STORAGE_PREFIX = 'abrsm:pdp-social-proof:view:';

function readStoredSocialProofViewTimestamp(handle) {
  if (!handle) return 0;

  try {
    const rawValue = window.localStorage.getItem(`${SOCIAL_PROOF_TRACK_STORAGE_PREFIX}${handle}`);
    const parsed = parseInt(rawValue || '', 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    return 0;
  }
}

function writeStoredSocialProofViewTimestamp(handle) {
  if (!handle) return;

  try {
    window.localStorage.setItem(`${SOCIAL_PROOF_TRACK_STORAGE_PREFIX}${handle}`, String(Date.now()));
  } catch (error) {
    // Ignore storage failures and allow future attempts.
  }
}

function shouldTrackSocialProofView(block, previewThemeContext) {
  if (!block || !SOCIAL_PROOF_TRACK_URL) {
    return false;
  }

  if (!readBooleanAttribute(block, 'data-pdp-social-proof-track-views')) {
    return false;
  }

  if (previewThemeContext || !SOCIAL_PROOF_TRACK_HOSTS.has(window.location.hostname)) {
    return false;
  }

  const handle = block.getAttribute('data-pdp-social-proof-track-handle');
  if (!handle) {
    return false;
  }

  return Date.now() - readStoredSocialProofViewTimestamp(handle) >= SOCIAL_PROOF_TRACK_TTL_MS;
}

async function sendSocialProofViewTrack(handle) {
  const payload = JSON.stringify({
    handle,
    path: window.location.pathname
  });

  let trackUrl;
  try {
    trackUrl = new URL(SOCIAL_PROOF_TRACK_URL, window.location.href);
  } catch (error) {
    trackUrl = null;
  }

  // Cross-origin beacon requests send credentials that the current Render API CORS policy rejects.
  const canUseBeacon =
    navigator.sendBeacon &&
    typeof Blob !== 'undefined' &&
    trackUrl &&
    trackUrl.origin === window.location.origin;

  if (canUseBeacon) {
    const queued = navigator.sendBeacon(
      trackUrl.href,
      new Blob([payload], { type: 'application/json' })
    );

    return queued;
  }

  const response = await fetch(trackUrl ? trackUrl.href : SOCIAL_PROOF_TRACK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: payload,
    mode: 'cors',
    credentials: 'omit',
    keepalive: true
  });

  return response.ok;
}

function queueSocialProofViewTrack(block, previewThemeContext) {
  if (!shouldTrackSocialProofView(block, previewThemeContext) || block._pdpViewTrackQueued) {
    return;
  }

  const handle = block.getAttribute('data-pdp-social-proof-track-handle');
  if (!handle) {
    return;
  }

  block._pdpViewTrackQueued = true;

  const scheduleTrack = () => {
    window.setTimeout(async () => {
      if (document.visibilityState === 'hidden') {
        block._pdpViewTrackQueued = false;
        queueSocialProofViewTrack(block, previewThemeContext);
        return;
      }

      try {
        const sent = await sendSocialProofViewTrack(handle);
        if (sent) {
          writeStoredSocialProofViewTimestamp(handle);
        } else {
          block._pdpViewTrackQueued = false;
        }
      } catch (error) {
        block._pdpViewTrackQueued = false;
      }
    }, SOCIAL_PROOF_TRACK_DELAY_MS);
  };

  if (document.visibilityState === 'hidden') {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      document.removeEventListener('visibilitychange', handleVisibility);
      scheduleTrack();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return;
  }

  scheduleTrack();
}

function renderSocialProofItem(item) {
  if (!item) return '';

  if (item.type === 'views') {
    return `
      <p class="pdp-social-proof__item" data-pdp-social-proof-item="${item.type}">
        <span class="pdp-social-proof__dot" aria-hidden="true"></span>
        <span class="pdp-social-proof__text">
          ${
            item.count === 1
              ? 'Viewed by <strong>1</strong> person recently'
              : `Viewed by <strong>${item.count}</strong> people recently`
          }
        </span>
      </p>
    `;
  }

  return `
    <p class="pdp-social-proof__item" data-pdp-social-proof-item="${item.type}">
      <span class="pdp-social-proof__dot" aria-hidden="true"></span>
      <span class="pdp-social-proof__text">
        ${
          item.count === 1
            ? 'Purchased <strong>1</strong> time recently'
            : `Purchased <strong>${item.count}</strong> times recently`
        }
      </span>
    </p>
  `;
}

function buildSocialProofItems({ showViews, viewsCount, showSales, salesCount }) {
  const items = [];

  if (showViews) {
    items.push({ type: 'views', count: viewsCount });
  }

  if (showSales) {
    items.push({ type: 'sales', count: salesCount });
  }

  return items;
}

function clearSocialProofCycle(block) {
  if (!block || !block._pdpSocialProofState) return;

  const { displayTimer, swapTimer } = block._pdpSocialProofState;
  window.clearTimeout(displayTimer);
  window.clearTimeout(swapTimer);
  delete block._pdpSocialProofState;
}

function startSocialProofCycle(block, items) {
  const body = block.querySelector('[data-pdp-social-proof-body]');
  if (!body || !items.length) return;

  clearSocialProofCycle(block);

  const state = {
    currentIndex: 0,
    displayTimer: 0,
    swapTimer: 0
  };
  const visibleDuration = 5400;
  const fadeDuration = 360;

  const renderCurrent = () => {
    const item = items[state.currentIndex];
    body.innerHTML = renderSocialProofItem(item);
    body.classList.remove('is-visible');
    body.setAttribute('data-pdp-social-proof-state', item.type);

    window.requestAnimationFrame(() => {
      body.classList.add('is-visible');
    });

    state.displayTimer = window.setTimeout(() => {
      body.classList.remove('is-visible');

      state.swapTimer = window.setTimeout(() => {
        state.currentIndex = (state.currentIndex + 1) % items.length;
        renderCurrent();
      }, fadeDuration);
    }, visibleDuration);
  };

  block._pdpSocialProofState = state;
  renderCurrent();
}

function initSocialProof(section) {
  const socialProofBlocks = Array.from(section.querySelectorAll('[data-pdp-social-proof]'));
  if (!socialProofBlocks.length) return;
  const previewThemeContext = isPreviewThemeContext();
  const inlineSlot = section.querySelector('[data-pdp-social-proof-slot]');
  const buySlot = section.querySelector('[data-pdp-social-proof-buy-slot]');
  const mediaWrap =
    section.querySelector('.product__main-photos') ||
    section.querySelector('.product-media-wrap') ||
    section.querySelector('.product-single__sticky');
  const mobileInlineQuery = window.matchMedia ? window.matchMedia('(max-width: 640px)') : null;

  function placeSocialProof() {
    const preferredInlineSlot =
      mobileInlineQuery && mobileInlineQuery.matches ? buySlot || inlineSlot : inlineSlot;

    if (preferredInlineSlot) {
      socialProofBlocks.forEach((block) => {
        block.classList.remove('pdp-social-proof--overlay');
        block.classList.add('pdp-social-proof--inline');
        block.classList.toggle('pdp-social-proof--buy', preferredInlineSlot === buySlot);

        if (block.parentElement !== preferredInlineSlot) {
          preferredInlineSlot.appendChild(block);
        }
      });
      return;
    }

    if (!mediaWrap) {
      return;
    }

    socialProofBlocks.forEach((block) => {
      block.classList.remove('pdp-social-proof--inline', 'pdp-social-proof--buy');
      if (block.parentElement !== mediaWrap) {
        block.classList.add('pdp-social-proof--overlay');
        mediaWrap.appendChild(block);
      }
    });
  }

  placeSocialProof();

  if (mobileInlineQuery) {
    const handleViewportChange = () => placeSocialProof();
    if (typeof mobileInlineQuery.addEventListener === 'function') {
      mobileInlineQuery.addEventListener('change', handleViewportChange);
    } else if (typeof mobileInlineQuery.addListener === 'function') {
      mobileInlineQuery.addListener(handleViewportChange);
    }
  }

  function syncVisibility(variant) {
    socialProofBlocks.forEach((block) => {
      const hideOnUnavailable = block.getAttribute('data-hide-on-unavailable-variant') === 'true';
      const isUnavailable = !variant || variant.available === false;
      const body = block.querySelector('[data-pdp-social-proof-body]');
      const previewEnabled = readBooleanAttribute(block, 'data-pdp-social-proof-preview-enabled') && previewThemeContext;

      const actualViewsVisible = readBooleanAttribute(block, 'data-pdp-social-proof-views-visible');
      const actualSalesVisible = readBooleanAttribute(block, 'data-pdp-social-proof-sales-visible');
      const previewViewsVisible = readBooleanAttribute(block, 'data-pdp-social-proof-preview-views-visible');
      const previewSalesVisible = readBooleanAttribute(block, 'data-pdp-social-proof-preview-sales-visible');

      const showViews = actualViewsVisible || (!actualViewsVisible && previewEnabled && previewViewsVisible);
      const showSales = actualSalesVisible || (!actualSalesVisible && previewEnabled && previewSalesVisible);
      const viewsCount = actualViewsVisible
        ? parseIntegerAttribute(block, 'data-pdp-social-proof-views-count')
        : parseIntegerAttribute(block, 'data-pdp-social-proof-preview-views-count');
      const salesCount = actualSalesVisible
        ? parseIntegerAttribute(block, 'data-pdp-social-proof-sales-count')
        : parseIntegerAttribute(block, 'data-pdp-social-proof-preview-sales-count');
      const items = buildSocialProofItems({ showViews, viewsCount, showSales, salesCount });
      const shouldHide = !items.length || (hideOnUnavailable && isUnavailable);

      if (shouldHide) {
        clearSocialProofCycle(block);
        if (body) {
          body.classList.remove('is-visible');
          body.innerHTML = '';
        }
        setVisibility(block, true);
        return;
      }

      startSocialProofCycle(block, items);
      setVisibility(block, false);
    });
  }

  let currentVariant = null;
  const currentVariantJson = section.querySelector('[data-current-variant-json]');

  if (currentVariantJson) {
    try {
      currentVariant = JSON.parse(currentVariantJson.textContent);
    } catch (error) {
      currentVariant = null;
    }
  }

  syncVisibility(currentVariant);

  const trackBlock = socialProofBlocks.find((block) =>
    readBooleanAttribute(block, 'data-pdp-social-proof-track-views')
  );
  if (trackBlock) {
    queueSocialProofViewTrack(trackBlock, previewThemeContext);
  }

  section.addEventListener('variantChange', (event) => {
    const variant = event && event.detail ? event.detail.variant : null;
    syncVisibility(variant);
  });
}

const syllabusIndexRequests = new Map();
const syllabusProductRequests = new Map();

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function readBooleanAttribute(element, attributeName) {
  return element.getAttribute(attributeName) === 'true';
}

function readPositiveIntegerAttribute(element, attributeName, fallbackValue) {
  const parsed = parseInt(element.getAttribute(attributeName) || '', 10);
  return Number.isNaN(parsed) || parsed < 1 ? fallbackValue : parsed;
}

function normalizeIdentity(value) {
  return String(value ?? '').trim().toLowerCase();
}

function inferTheoryGradeKey(productHandle, productTitle) {
  const handleMatch = normalizeIdentity(productHandle).match(/grade-(initial|\d+)/);
  if (handleMatch && handleMatch[1]) {
    return handleMatch[1];
  }

  const titleMatch = String(productTitle ?? '').match(/grade\s+(initial|\d+)/i);
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].toLowerCase();
  }

  return '';
}

function inferTheoryProductData(payload, productHandle, productTitle) {
  const identity = normalizeIdentity(`${productHandle} ${productTitle}`);
  const looksLikeTheory =
    identity.includes('music-theory') ||
    identity.includes('theory-workbook') ||
    identity.includes('music theory') ||
    identity.includes('ab guide') ||
    identity.includes('ab-guide') ||
    identity.includes('guide to music theory') ||
    identity.includes('first-steps-in-music-theory');

  if (!looksLikeTheory) {
    return null;
  }

  const gradeKey = inferTheoryGradeKey(productHandle, productTitle);
  const theoryGrades = payload && payload.theory && payload.theory.grades ? payload.theory.grades : null;
  const gradeData = theoryGrades && gradeKey ? theoryGrades[gradeKey] : null;
  if (!gradeData) {
    return null;
  }

  const isSupporting =
    identity.includes('model-answer') ||
    identity.includes('workbook') ||
    identity.includes('ab guide') ||
    identity.includes('ab-guide') ||
    identity.includes('guide to music theory') ||
    identity.includes('first-steps');

  return {
    h: isSupporting ? gradeData.sh : gradeData.h,
    c: gradeData.c ? [gradeData.c] : [],
    r: Array.isArray(gradeData.r) ? gradeData.r : []
  };
}

function resolveSyllabusProductData(payload, productHandle, productTitle) {
  const theoryFallback = inferTheoryProductData(payload, productHandle, productTitle);
  if (theoryFallback) {
    return theoryFallback;
  }

  return payload && payload.products ? payload.products[productHandle] : null;
}

const syllabusRouteOrder = ['practical', 'performance'];
const syllabusMedalOrder = ['bronze', 'silver', 'gold', 'platinum'];

function uniqueSortedNumeric(values) {
  return Array.from(new Set(values)).sort((left, right) => left - right);
}

function joinNaturalLabels(values) {
  if (!values.length) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function formatSyllabusGradeValue(value) {
  return value === 0 ? 'Initial' : String(value);
}

function formatSyllabusGradeDescriptor(grades) {
  const orderedGrades = uniqueSortedNumeric(grades);

  if (!orderedGrades.length) {
    return '';
  }

  if (orderedGrades.length === 1) {
    return `grade ${formatSyllabusGradeValue(orderedGrades[0])}`;
  }

  const isContinuous = orderedGrades.every((grade, index) => {
    if (index === 0) return true;
    return grade === orderedGrades[index - 1] + 1;
  });

  if (isContinuous) {
    return `grades ${formatSyllabusGradeValue(orderedGrades[0])} to ${formatSyllabusGradeValue(
      orderedGrades[orderedGrades.length - 1]
    )}`;
  }

  return `grades ${joinNaturalLabels(orderedGrades.map(formatSyllabusGradeValue))}`;
}

function formatSyllabusMedalDescriptor(levels) {
  const orderedLevels = Array.from(new Set(levels))
    .sort((left, right) => syllabusMedalOrder.indexOf(left) - syllabusMedalOrder.indexOf(right));

  if (!orderedLevels.length) {
    return '';
  }

  if (orderedLevels.length === 1) {
    return orderedLevels[0];
  }

  const orderedIndexes = orderedLevels.map((level) => syllabusMedalOrder.indexOf(level));
  const isContinuous = orderedIndexes.every((levelIndex, index) => {
    if (index === 0) return true;
    return levelIndex === orderedIndexes[index - 1] + 1;
  });

  if (isContinuous) {
    return `${orderedLevels[0]} to ${orderedLevels[orderedLevels.length - 1]}`;
  }

  return joinNaturalLabels(orderedLevels);
}

function parseSyllabusContextLabel(label) {
  const rawLabel = String(label || '').trim();
  if (!rawLabel) {
    return { kind: 'raw', label: '' };
  }

  const gradeMatch = rawLabel.match(/^(.+?)\s+(practical|performance)\s+grade\s+(initial|\d+)$/i);
  if (gradeMatch) {
    const gradeToken = String(gradeMatch[3]).toLowerCase();
    const numericGrade = gradeToken === 'initial' ? 0 : parseInt(gradeToken, 10);

    if (!Number.isNaN(numericGrade)) {
      return {
        kind: 'grade',
        label: rawLabel,
        instrument: gradeMatch[1].trim(),
        route: String(gradeMatch[2]).toLowerCase(),
        grade: numericGrade
      };
    }
  }

  const medalsMatch = rawLabel.match(/^(.+?)\s+Music Medals\s+(bronze|silver|gold|platinum)$/i);
  if (medalsMatch) {
    return {
      kind: 'medals',
      label: rawLabel,
      instrument: medalsMatch[1].trim(),
      level: String(medalsMatch[2]).toLowerCase()
    };
  }

  return { kind: 'raw', label: rawLabel };
}

function compressSyllabusContextLabels(contextLabels) {
  const parsedLabels = (Array.isArray(contextLabels) ? contextLabels : [])
    .map((label, index) => ({ index, ...parseSyllabusContextLabel(label) }))
    .filter((entry) => entry.label);

  const gradeGroups = new Map();
  const medalGroups = new Map();
  const rawGroups = [];

  parsedLabels.forEach((entry) => {
    if (entry.kind === 'grade') {
      const key = `${normalizeIdentity(entry.instrument)}::${entry.route}`;
      if (!gradeGroups.has(key)) {
        gradeGroups.set(key, {
          instrument: entry.instrument,
          route: entry.route,
          grades: [],
          firstIndex: entry.index
        });
      }

      const group = gradeGroups.get(key);
      group.grades.push(entry.grade);
      group.firstIndex = Math.min(group.firstIndex, entry.index);
      return;
    }

    if (entry.kind === 'medals') {
      const key = normalizeIdentity(entry.instrument);
      if (!medalGroups.has(key)) {
        medalGroups.set(key, {
          instrument: entry.instrument,
          levels: [],
          firstIndex: entry.index
        });
      }

      const group = medalGroups.get(key);
      group.levels.push(entry.level);
      group.firstIndex = Math.min(group.firstIndex, entry.index);
      return;
    }

    rawGroups.push({ label: entry.label, firstIndex: entry.index });
  });

  const combinedGradeGroups = new Map();
  gradeGroups.forEach((group) => {
    const grades = uniqueSortedNumeric(group.grades);
    const gradeKey = grades.join('|');
    const combinedKey = `${normalizeIdentity(group.instrument)}::${gradeKey}`;

    if (!combinedGradeGroups.has(combinedKey)) {
      combinedGradeGroups.set(combinedKey, {
        instrument: group.instrument,
        grades,
        routes: [],
        firstIndex: group.firstIndex
      });
    }

    const combinedGroup = combinedGradeGroups.get(combinedKey);
    combinedGroup.routes.push(group.route);
    combinedGroup.firstIndex = Math.min(combinedGroup.firstIndex, group.firstIndex);
  });

  const compressedLabels = [];

  combinedGradeGroups.forEach((group) => {
    const orderedRoutes = Array.from(new Set(group.routes)).sort(
      (left, right) => syllabusRouteOrder.indexOf(left) - syllabusRouteOrder.indexOf(right)
    );
    compressedLabels.push({
      firstIndex: group.firstIndex,
      label: `${group.instrument} ${joinNaturalLabels(orderedRoutes)} ${formatSyllabusGradeDescriptor(
        group.grades
      )}`.trim()
    });
  });

  medalGroups.forEach((group) => {
    compressedLabels.push({
      firstIndex: group.firstIndex,
      label: `${group.instrument} Music Medals ${formatSyllabusMedalDescriptor(group.levels)}`.trim()
    });
  });

  rawGroups.forEach((group) => {
    compressedLabels.push(group);
  });

  return compressedLabels
    .sort((left, right) => left.firstIndex - right.firstIndex)
    .map((entry) => entry.label);
}

function findSyllabusTabParts(block) {
  const content = block.closest('[data-pdp-syllabus-tab-content]');
  if (!content) return null;

  let label = content.previousElementSibling;
  let input = label ? label.previousElementSibling : null;

  if (!label || !label.hasAttribute('data-pdp-syllabus-tab-label')) {
    label = null;
  }

  if (!input || !input.hasAttribute('data-pdp-syllabus-tab-input')) {
    input = null;
  }

  return input && label ? { input, label, content } : null;
}

function removeSyllabusTab(block) {
  const tabParts = findSyllabusTabParts(block);
  if (!tabParts) return;

  const { input, label, content } = tabParts;
  const wasChecked = input.checked;
  const tabBar = content.closest('.product-tab-bar');
  input.remove();
  label.remove();
  content.remove();

  if (wasChecked) {
    const nextTab = tabBar?.querySelector('.product-tab-input:not([hidden])');

    if (nextTab) {
      nextTab.checked = true;
      nextTab.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

function activateSyllabusTab(section) {
  const input = section.querySelector('[data-pdp-syllabus-tab-input]');
  const label = section.querySelector('[data-pdp-syllabus-tab-label]');
  const content = section.querySelector('[data-pdp-syllabus-tab-content]');

  if (!(input && label && content)) {
    return false;
  }

  input.checked = true;
  input.dispatchEvent(new Event('change', { bubbles: true }));

  const scrollTarget = label.closest('.product-tabs-wrapper') || label;
  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return true;
}

function dispatchSyllabusCartAdded(section, sourceButton, payload) {
  const eventTarget = section.querySelector('.product-single__form') || section;
  eventTarget.dispatchEvent(
    new CustomEvent('ajaxProduct:added', {
      detail: {
        product: payload,
        addToCartBtn: sourceButton || null
      },
      bubbles: true
    })
  );
}

function extractProductHandleFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/^\/products\/([^/?#]+)/);
    return match && match[1] ? decodeURIComponent(match[1]) : '';
  } catch (error) {
    return '';
  }
}

const syllabusHandleAliases = {
  'aural-training-in-practice-abrsm-grades-1-3-with-cd': 'aural-training-in-practice-abrsm-grades-1-3'
};

function resolveSyllabusProductHandle(handle) {
  const normalizedHandle = String(handle || '').trim();
  return syllabusHandleAliases[normalizedHandle] || normalizedHandle;
}

function loadProductJsonByHandle(handle) {
  return fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .then((productPayload) => {
      if (productPayload && !productPayload.handle) {
        productPayload.handle = handle;
      }

      return productPayload;
    })
    .catch(() => null);
}

function loadProductByHandle(handle) {
  if (!handle || !('fetch' in window)) {
    return Promise.resolve(null);
  }

  const requestHandle = resolveSyllabusProductHandle(handle);

  if (!syllabusProductRequests.has(requestHandle)) {
    const request = (async () => {
      const normalizedHandle = requestHandle;
      if (!normalizedHandle) {
        return null;
      }

      const directPayload = await loadProductJsonByHandle(normalizedHandle);
      if (directPayload) {
        return directPayload;
      }

      const htmlResponse = await fetch(`/products/${encodeURIComponent(normalizedHandle)}`, {
        credentials: 'same-origin'
      }).catch(() => null);

      if (!(htmlResponse && htmlResponse.ok)) {
        return null;
      }

      const canonicalHandle = extractProductHandleFromUrl(htmlResponse.url);
      if (!canonicalHandle || canonicalHandle === normalizedHandle) {
        return null;
      }

      return loadProductJsonByHandle(canonicalHandle);
    })();

    syllabusProductRequests.set(requestHandle, request);
  }

  return syllabusProductRequests.get(requestHandle);
}

function getFirstAvailableVariant(productPayload) {
  if (!productPayload || !Array.isArray(productPayload.variants)) {
    return null;
  }

  return (
    productPayload.variants.find((variant) => variant && variant.available) ||
    productPayload.variants[0] ||
    null
  );
}

async function addSyllabusHandlesToCart(handles) {
  const uniqueHandles = Array.from(new Set((handles || []).filter(Boolean)));
  if (!uniqueHandles.length) {
    throw new Error('No titles are available to add right now.');
  }

  const loadedProducts = await Promise.all(uniqueHandles.map((handle) => loadProductByHandle(handle)));
  const items = loadedProducts
    .map((productPayload) => {
      const variant = getFirstAvailableVariant(productPayload);
      return variant ? { id: variant.id, quantity: 1 } : null;
    })
    .filter(Boolean);

  if (!items.length) {
    throw new Error('These titles are currently unavailable.');
  }

  const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  const response = await fetch(cartAddUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json'
    },
    body: JSON.stringify({ items })
  });
  const payload = await response.json();

  if (!response.ok || payload.status === 422) {
    throw new Error(payload.description || payload.message || 'Could not add these titles right now.');
  }

  return {
    payload,
    addedCount: items.length
  };
}

function loadSyllabusIndex(url) {
  if (!url || !('fetch' in window)) {
    return Promise.resolve(null);
  }

  if (!syllabusIndexRequests.has(url)) {
    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    syllabusIndexRequests.set(url, request);
  }

  return syllabusIndexRequests.get(url);
}

function getCompactSyllabusSummaryTitle(headline) {
  if (typeof headline !== 'string') {
    return '';
  }

  return headline
    .replace(/^Recommended title for\s+/i, 'Recommended for ')
    .replace(/\s+(practical|performance)\s+grade\s+/i, ' grade ');
}

function renderCompactSyllabusSummaryLine(labels, moreCount) {
  if (!Array.isArray(labels) || !labels.length) {
    return '';
  }

  const joinedLabels = labels
    .map((label) => `<span class="pdp-syllabus-context__summary-fragment">${escapeHtml(label)}</span>`)
    .join('<span class="pdp-syllabus-context__summary-separator" aria-hidden="true"> • </span>');
  const moreMarkup = moreCount
    ? `<span class="pdp-syllabus-context__summary-more">+${moreCount} more</span>`
    : '';

  return `<p class="pdp-syllabus-context__summary-line">${joinedLabels}${moreMarkup ? ` <span class="pdp-syllabus-context__summary-more-wrap">${moreMarkup}</span>` : ''}</p>`;
}

function renderSyllabusContext(productData, options) {
  const contextLabels = Array.isArray(productData.c) ? productData.c : [];
  const groupedContextLabels = compressSyllabusContextLabels(contextLabels);
  const relatedProducts = Array.isArray(productData.r) ? productData.r : [];
  const surface = options.surface || 'summary';
  const showContextChips = Boolean(options.showContextChips) && groupedContextLabels.length > 0;
  const maxSummaryContextChips =
    Number.isFinite(options.maxContextChips) && options.maxContextChips > 0
      ? Math.min(options.maxContextChips, 3)
      : 3;
  const maxTabContextChips =
    Number.isFinite(options.maxTabContextChips) && options.maxTabContextChips > 0
      ? options.maxTabContextChips
      : 6;
  const maxTabContextChipsMobile =
    Number.isFinite(options.maxTabContextChipsMobile) && options.maxTabContextChipsMobile > 0
      ? options.maxTabContextChipsMobile
      : 3;
  const useMobileTabDensity =
    surface === 'tab' &&
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 640px)').matches;
  const activeMaxTabContextChips = useMobileTabDensity ? maxTabContextChipsMobile : maxTabContextChips;
  const displayedContextLabels = showContextChips
    ? surface === 'tab'
      ? groupedContextLabels.slice(0, activeMaxTabContextChips)
      : groupedContextLabels.slice(0, maxSummaryContextChips)
    : [];
  const hiddenContextLabels =
    showContextChips && surface === 'tab' ? groupedContextLabels.slice(activeMaxTabContextChips) : [];
  const maxRelatedTitles =
    Number.isFinite(options.maxRelatedTitles) && options.maxRelatedTitles > 0
      ? options.maxRelatedTitles
      : relatedProducts.length;
  const visibleRelatedProducts = options.showRelatedTitles ? relatedProducts.slice(0, maxRelatedTitles) : [];
  const moreContextsCount = Math.max(groupedContextLabels.length - displayedContextLabels.length, 0);
  const hasCompressedMatches = groupedContextLabels.length < contextLabels.length;
  const moreRelatedCount = Math.max(relatedProducts.length - visibleRelatedProducts.length, 0);
  const relatedTitle = options.relatedTitle || 'Recommended support titles';
  const addLabel = options.addLabel || 'Add';
  const addAllLabel = options.addAllLabel || 'Add all';
  const seeMoreLabel = options.seeMoreLabel || 'See more';
  const seeAllMatchesLabel = options.seeAllMatchesLabel || 'See more syllabus matches';
  const productPayloads = options.productPayloads || {};
  const matchesLabel =
    contextLabels.length === 1 ? '1 syllabus match' : `Covers ${contextLabels.length} syllabus matches`;
  const compactSummaryLineMarkup =
    surface === 'summary'
      ? renderCompactSyllabusSummaryLine(
          groupedContextLabels.slice(0, maxSummaryContextChips),
          Math.max(groupedContextLabels.length - maxSummaryContextChips, 0)
        )
      : '';
  const summaryTitle = surface === 'summary' ? getCompactSyllabusSummaryTitle(productData.h) : productData.h;

  const chipsMarkup = displayedContextLabels.length
    ? `
        <ul class="pdp-syllabus-context__chips${surface === 'tab' ? ' pdp-syllabus-context__chips--all' : ''}" role="list">
          ${displayedContextLabels
            .map((label) => `<li><span class="pdp-syllabus-context__chip">${escapeHtml(label)}</span></li>`)
            .join('')}
          ${
            surface !== 'tab' && moreContextsCount
              ? `<li><span class="pdp-syllabus-context__chip pdp-syllabus-context__chip--more">+${moreContextsCount} more</span></li>`
              : ''
          }
        </ul>
      `
    : '';

  const relatedActions = [];

  if (surface === 'tab' && visibleRelatedProducts.length > 1) {
    relatedActions.push(
      `<button type="button" class="pdp-syllabus-context__add-all" data-pdp-syllabus-add-all>${escapeHtml(addAllLabel)}</button>`
    );
  }

  if (surface === 'teaser' && moreRelatedCount > 0 && options.hasTab) {
    relatedActions.push(
      `<button type="button" class="pdp-syllabus-context__more-button" data-pdp-syllabus-open-tab>${escapeHtml(seeMoreLabel)}</button>`
    );
  }

  const summaryActions = [];

  if (surface === 'summary' && (moreContextsCount > 0 || hasCompressedMatches) && options.hasTab) {
    summaryActions.push(
      `<button type="button" class="pdp-syllabus-context__more-button pdp-syllabus-context__more-button--summary" data-pdp-syllabus-open-tab>${escapeHtml(seeAllMatchesLabel)}</button>`
    );
  }

  const hiddenChipsMarkup = hiddenContextLabels.length
    ? `
        <details class="pdp-syllabus-context__more-matches">
          <summary class="pdp-syllabus-context__more-matches-toggle">
            <span class="pdp-syllabus-context__more-matches-label pdp-syllabus-context__more-matches-label--more">See more matches</span>
            <span class="pdp-syllabus-context__more-matches-label pdp-syllabus-context__more-matches-label--less">See fewer matches</span>
            <span class="pdp-syllabus-context__more-matches-icon" aria-hidden="true"></span>
          </summary>
          <ul class="pdp-syllabus-context__chips pdp-syllabus-context__chips--all" role="list">
            ${hiddenContextLabels
              .map((label) => `<li><span class="pdp-syllabus-context__chip">${escapeHtml(label)}</span></li>`)
              .join('')}
          </ul>
        </details>
      `
    : '';

  const relatedMarkup = visibleRelatedProducts.length
    ? `
        <div class="pdp-syllabus-context__related">
          <div class="pdp-syllabus-context__related-header">
            <p class="pdp-syllabus-context__related-title">${escapeHtml(relatedTitle)}</p>
            ${relatedActions.join('')}
          </div>
          <div class="pdp-syllabus-context__items">
            ${visibleRelatedProducts
              .map(([handle, title]) => {
                const productPayload = productPayloads[handle] || null;
                const resolvedHandle = (productPayload && productPayload.handle) || handle;
                const href = `/products/${encodeURIComponent(resolvedHandle)}`;
                const productImage =
                  productPayload && (productPayload.featured_image || (Array.isArray(productPayload.images) && productPayload.images[0]));
                const thumbMarkup = productImage
                  ? `
                      <span class="pdp-syllabus-context__thumb" aria-hidden="true">
                        <img src="${escapeHtml(productImage)}" alt="" loading="lazy">
                      </span>
                    `
                  : `
                      <span class="pdp-syllabus-context__thumb pdp-syllabus-context__thumb--placeholder" aria-hidden="true">
                        ${escapeHtml((title || '').trim().charAt(0) || 'A')}
                      </span>
                    `;
                const titleMarkup =
                  options.linkRelatedTitles === false
                    ? `
                        <div class="pdp-syllabus-context__item-link pdp-syllabus-context__item-link--static">
                          ${thumbMarkup}
                          <span class="pdp-syllabus-context__item-copy">${escapeHtml(title)}</span>
                        </div>
                      `
                    : `
                        <a class="pdp-syllabus-context__item-link" href="${href}">
                          ${thumbMarkup}
                          <span class="pdp-syllabus-context__item-copy">${escapeHtml(title)}</span>
                        </a>
                      `;

                return `
                  <div class="pdp-syllabus-context__item" data-pdp-syllabus-item>
                    ${titleMarkup}
                    <button
                      type="button"
                      class="pdp-syllabus-context__add-button"
                      data-pdp-syllabus-add
                      data-pdp-syllabus-handle="${escapeHtml(resolvedHandle)}"
                      data-pdp-syllabus-product-title="${escapeHtml(title)}"
                    >${escapeHtml(addLabel)}</button>
                  </div>
                `;
              })
              .join('')}
          </div>
          <p class="pdp-syllabus-context__status" data-pdp-syllabus-status aria-live="polite"></p>
        </div>
      `
    : '';

  const summaryMarkup =
    surface === 'tab'
      ? `
          <div class="pdp-syllabus-context__summary pdp-syllabus-context__summary--tab">
            <p class="pdp-syllabus-context__eyebrow">Syllabus matches</p>
            <p class="pdp-syllabus-context__title pdp-syllabus-context__title--tab">${escapeHtml(productData.h)}</p>
            ${
              chipsMarkup
                ? `
                    <div class="pdp-syllabus-context__matches">
                      <p class="pdp-syllabus-context__matches-label">${escapeHtml(matchesLabel)}</p>
                      ${chipsMarkup}
                      ${hiddenChipsMarkup}
                    </div>
                  `
                : ''
            }
          </div>
        `
      : `
          <div class="pdp-syllabus-context__summary">
            <p class="pdp-syllabus-context__eyebrow">Syllabus match</p>
            <p class="pdp-syllabus-context__title pdp-syllabus-context__title--summary">${escapeHtml(summaryTitle)}</p>
            ${compactSummaryLineMarkup}
            ${
              summaryActions.length
                ? `<div class="pdp-syllabus-context__summary-actions">${summaryActions.join('')}</div>`
                : ''
            }
          </div>
        `;

  if (surface === 'summary') {
    return `
      <div class="pdp-syllabus-context__card pdp-syllabus-context__card--summary">
        ${summaryMarkup}
      </div>
    `;
  }

  if (surface === 'teaser') {
    return `
      <div class="pdp-syllabus-context__card pdp-syllabus-context__card--teaser">
        ${relatedMarkup}
      </div>
    `;
  }

  return `
    <div class="pdp-syllabus-context__card${surface === 'tab' ? ' pdp-syllabus-context__card--tab' : ''}">
      ${summaryMarkup}
      ${relatedMarkup}
    </div>
  `;
}

function initSyllabusContext(section) {
  const syllabusBlocks = Array.from(section.querySelectorAll('[data-pdp-syllabus-context]'));
  if (!syllabusBlocks.length) return;

  syllabusBlocks.forEach(async (block) => {
    const body = block.querySelector('[data-pdp-syllabus-context-body]');
    const url = block.getAttribute('data-pdp-syllabus-context-url');
    const productHandle = block.getAttribute('data-pdp-syllabus-handle');
    const productTitle = block.getAttribute('data-pdp-syllabus-title') || '';
    const surface = block.getAttribute('data-pdp-syllabus-surface') || 'block';

    if (!(body && url && productHandle)) {
      if (surface === 'tab') {
        removeSyllabusTab(block);
      } else {
        setVisibility(block, true);
      }
      return;
    }

    const payload = await loadSyllabusIndex(url);
    const productData = resolveSyllabusProductData(payload, productHandle, productTitle);
    const allRelatedProducts = productData && Array.isArray(productData.r) ? productData.r : [];
    const showRelatedTitles = readBooleanAttribute(block, 'data-pdp-syllabus-show-related') && surface !== 'summary';
    const maxRelatedTitles =
      surface === 'tab'
        ? allRelatedProducts.length
        : readPositiveIntegerAttribute(block, 'data-pdp-syllabus-max-related', 4);
    const preloadHandles = showRelatedTitles
      ? allRelatedProducts
          .slice(0, maxRelatedTitles)
          .map((entry) => (Array.isArray(entry) ? entry[0] : null))
          .filter(Boolean)
      : [];

    if (!productData || !productData.h || ((surface === 'tab' || surface === 'teaser') && !allRelatedProducts.length)) {
      if (surface === 'tab') {
        removeSyllabusTab(block);
      } else {
        setVisibility(block, true);
      }
      return;
    }

    const relatedProductsPayloads = await Promise.all(preloadHandles.map((handle) => loadProductByHandle(handle)));
    const productPayloads = preloadHandles.reduce((accumulator, handle, index) => {
      if (relatedProductsPayloads[index]) {
        accumulator[handle] = relatedProductsPayloads[index];
      }
      return accumulator;
    }, {});

    body.innerHTML = renderSyllabusContext(productData, {
      surface,
      showContextChips: readBooleanAttribute(block, 'data-pdp-syllabus-show-context-chips'),
      maxContextChips: readPositiveIntegerAttribute(block, 'data-pdp-syllabus-max-context-chips', 4),
      maxTabContextChips: readPositiveIntegerAttribute(block, 'data-pdp-syllabus-max-tab-context-chips', 6),
      maxTabContextChipsMobile: readPositiveIntegerAttribute(
        block,
        'data-pdp-syllabus-max-tab-context-chips-mobile',
        3
      ),
      showRelatedTitles,
      maxRelatedTitles,
      relatedTitle: block.getAttribute('data-pdp-syllabus-related-title') || '',
      addLabel: block.getAttribute('data-pdp-syllabus-add-label') || 'Add',
      addAllLabel: block.getAttribute('data-pdp-syllabus-add-all-label') || 'Add all',
      seeMoreLabel: block.getAttribute('data-pdp-syllabus-see-more-label') || 'See more',
      seeAllMatchesLabel: block.getAttribute('data-pdp-syllabus-see-all-label') || 'See more syllabus matches',
      hasTab: Boolean(section.querySelector('[data-pdp-syllabus-tab-input]')),
      productPayloads,
      linkRelatedTitles: surface !== 'tab'
    });
    setVisibility(block, false);

    const status = body.querySelector('[data-pdp-syllabus-status]');
    const addButtons = Array.from(body.querySelectorAll('[data-pdp-syllabus-add]'));
    const addAllButton = body.querySelector('[data-pdp-syllabus-add-all]');
    const openTabButton = body.querySelector('[data-pdp-syllabus-open-tab]');

    function setButtonState(button, state) {
      if (!button) return;

      const defaultLabel =
        button.getAttribute('data-pdp-syllabus-default-label') ||
        button.textContent.trim() ||
        (button.hasAttribute('data-pdp-syllabus-add-all') ? 'Add all' : 'Add');
      button.setAttribute('data-pdp-syllabus-default-label', defaultLabel);
      button.disabled = state === 'loading';

      if (state === 'loading') {
        button.textContent = 'Adding...';
      } else if (state === 'added') {
        button.textContent = 'Added';
      } else {
        button.textContent = defaultLabel;
      }
    }

    async function handleAdd(button, handles, successMessage) {
      setButtonState(button, 'loading');
      if (status) {
        status.textContent = 'Adding to basket...';
      }

      try {
        const result = await addSyllabusHandlesToCart(handles);
        setButtonState(button, 'added');
        if (status) {
          status.textContent = successMessage || `Added ${result.addedCount} title${result.addedCount === 1 ? '' : 's'} to your basket.`;
        }

        if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
          window.location = (window.theme.routes && window.theme.routes.cartPage) || '/cart';
          return;
        }

        dispatchSyllabusCartAdded(section, button, result.payload);
        window.setTimeout(() => setButtonState(button, 'idle'), 1800);
      } catch (error) {
        setButtonState(button, 'idle');
        if (status) {
          status.textContent = error && error.message ? error.message : 'Could not add this title right now.';
        }
      }
    }

    addButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const handle = button.getAttribute('data-pdp-syllabus-handle');
        const title = button.getAttribute('data-pdp-syllabus-product-title') || 'This title';
        if (!handle) return;
        handleAdd(button, [handle], `${title} was added to your basket.`);
      });
    });

    if (addAllButton) {
      addAllButton.addEventListener('click', () => {
        const handles = addButtons
          .map((button) => button.getAttribute('data-pdp-syllabus-handle'))
          .filter(Boolean);
        handleAdd(addAllButton, handles);
      });
    }

    if (openTabButton) {
      openTabButton.addEventListener('click', () => {
        activateSyllabusTab(section);
      });
    }
  });
}

function initAtcBar(section) {
  const atcRoot = section.querySelector('[data-atc-root]');
  if (!atcRoot) return;

  const formId = atcRoot.getAttribute('data-form-id');
  const form = formId ? document.getElementById(formId) : null;
  const qtyWrap = atcRoot.querySelector('[data-qty]');
  const input = atcRoot.querySelector('[data-qty-input]');
  const atcSlot = atcRoot.querySelector('[data-atc-slot]');
  const maxAttr = atcRoot.getAttribute('data-max');

  if (!(form && qtyWrap && input && atcSlot)) return;

  if (maxAttr) input.setAttribute('max', maxAttr);
  input.setAttribute('name', 'quantity');

  const submit = form.querySelector('[type="submit"], .product-form__cart-submit');
  if (submit) {
    form.insertBefore(atcRoot, submit);
    atcSlot.appendChild(submit);

    if (section.getAttribute('data-atc-show-icon') === 'true' && !submit.querySelector('.pdp-cart-icon')) {
      const span = document.createElement('span');
      span.className = 'pdp-cart-icon';
      span.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M7 18a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM4.2 4h-.9a1 1 0 1 1 0-2h2.3c.46 0 .86.31.97.76L7.3 7h11.94a1 1 0 0 1 .98 1.2l-1.3 6.5a2 2 0 0 1-1.96 1.6H8.39a2 2 0 0 1-1.96-1.6l-1.5-7.4L4.2 4Z"/></svg>';
      submit.prepend(span);
    }
  }

  function clampQuantity(value) {
    let quantity = parseInt(value || '1', 10);
    if (isNaN(quantity) || quantity < 1) quantity = 1;

    if (maxAttr) {
      const max = parseInt(maxAttr, 10);
      if (!isNaN(max) && quantity > max) quantity = max;
    }

    return quantity;
  }

  const down = qtyWrap.querySelector('[data-qty-down]');
  const up = qtyWrap.querySelector('[data-qty-up]');

  if (down) {
    down.addEventListener('click', () => {
      input.value = String(clampQuantity((parseInt(input.value || '1', 10) || 1) - 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  if (up) {
    up.addEventListener('click', () => {
      input.value = String(clampQuantity((parseInt(input.value || '1', 10) || 1) + 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  input.addEventListener('input', () => {
    input.value = String(clampQuantity(input.value));
  });
}

function initMobileProductBlockOrder(section) {
  if (!('matchMedia' in window)) return;

  const blocksHolder = section.querySelector('[data-product-blocks]');
  const buyButtonsBlock = blocksHolder ? blocksHolder.querySelector('.product-block--buy-buttons') : null;
  const syllabusSummaryBlock = blocksHolder ? blocksHolder.querySelector('.product-block--syllabus-context') : null;
  if (!(blocksHolder && buyButtonsBlock && syllabusSummaryBlock)) return;

  if (!buyButtonsBlock._pdpOriginalAnchor) {
    const anchor = document.createComment('pdp-buy-buttons-anchor');
    blocksHolder.insertBefore(anchor, buyButtonsBlock);
    buyButtonsBlock._pdpOriginalAnchor = anchor;
  }

  const anchor = buyButtonsBlock._pdpOriginalAnchor;
  const mql = window.matchMedia('(max-width: 768px)');

  const syncOrder = () => {
    if (mql.matches) {
      if (buyButtonsBlock.nextElementSibling !== syllabusSummaryBlock) {
        blocksHolder.insertBefore(buyButtonsBlock, syllabusSummaryBlock);
      }
      return;
    }

    if (anchor.parentNode === blocksHolder && anchor.nextSibling !== buyButtonsBlock) {
      blocksHolder.insertBefore(buyButtonsBlock, anchor.nextSibling);
    }
  };

  syncOrder();

  if (!section._pdpMobileOrderListenerAttached) {
    mql.addEventListener('change', syncOrder);
    section._pdpMobileOrderListenerAttached = true;
  }
}

function initHoverSwap(section) {
  const wrap = section.querySelector('[data-media-wrap][data-hover-swap="true"]');
  if (!wrap) return;

  const allImages = Array.from(wrap.querySelectorAll('img'));
  const mainImages = allImages.filter((img) => {
    if (img.closest('.product-single__thumbnails, .thumbnails, [data-product-single-thumbnails], .product__thumb, .thumbnail')) {
      return false;
    }

    const width = parseInt(img.getAttribute('width') || '0', 10);
    return (
      width >= 400 ||
      img.className.indexOf('product__photo') > -1 ||
      img.closest('.product__media') ||
      img.closest('[data-product-photos]') ||
      img.closest('.product__slides')
    );
  });

  if (mainImages.length < 2) return;

  const primary = mainImages[0];
  const secondary = mainImages[1];
  const originalSrc = primary.currentSrc || primary.getAttribute('src');
  const originalSrcset = primary.getAttribute('srcset');
  const secondarySrc = secondary.currentSrc || secondary.getAttribute('data-src') || secondary.getAttribute('src');
  const secondarySrcset = secondary.getAttribute('data-srcset') || secondary.getAttribute('srcset');

  if (!secondarySrc) return;

  const preload = new Image();
  preload.src = secondarySrc;

  wrap.addEventListener('mouseenter', () => {
    if (secondarySrcset) primary.setAttribute('srcset', secondarySrcset);
    primary.setAttribute('src', secondarySrc);
  });

  wrap.addEventListener('mouseleave', () => {
    if (originalSrcset) primary.setAttribute('srcset', originalSrcset);
    if (originalSrc) primary.setAttribute('src', originalSrc);
  });
}

function initTabEmbeds(section) {
  const hydrateCheckedTab = () => {
    const checkedTab = section.querySelector('.product-tab-input:checked');
    const checkedContent = getAssociatedTabContent(checkedTab);
    if (checkedContent) hydrateVisibleEmbeds(checkedContent);
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(hydrateCheckedTab, { timeout: 3000 });
  } else {
    window.setTimeout(hydrateCheckedTab, 1500);
  }

  section.addEventListener('change', (event) => {
    if (!event.target || !event.target.classList.contains('product-tab-input')) return;

    const tabContent = getAssociatedTabContent(event.target);
    if (!tabContent) return;

    hydrateVisibleEmbeds(tabContent);
  });
}

function initTabKeyboard(section) {
  const tabBar = section.querySelector('.product-tab-bar');
  if (!tabBar) return;

  tabBar.addEventListener('keydown', (event) => {
    if (!event.target.classList.contains('product-tab-label')) return;

    const labels = Array.from(tabBar.querySelectorAll('.product-tab-label'));
    const currentIndex = labels.indexOf(event.target);
    let newIndex = -1;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      newIndex = (currentIndex + 1) % labels.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      newIndex = (currentIndex - 1 + labels.length) % labels.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      newIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      newIndex = labels.length - 1;
    }

    if (newIndex >= 0 && labels[newIndex]) {
      const newLabel = labels[newIndex];
      const radioInput = newLabel.previousElementSibling;

      if (radioInput && radioInput.classList.contains('product-tab-input')) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      newLabel.focus();
    }
  });

  tabBar.querySelectorAll('.product-tab-label').forEach((label) => {
    label.setAttribute('tabindex', '0');
  });
}

function initStickyAtc(section) {
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const atcHost = section.querySelector('[data-atc-root]');
  if (!stickyBar || !atcHost) return;

  const mql = window.matchMedia('(max-width: 768px)');
  if (!mql.matches) return;

  const stickyBtn = stickyBar.querySelector('[data-sticky-atc-btn]');

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('pdp-sticky-atc--visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            stickyBar.classList.add('pdp-sticky-atc--visible');
            stickyBar.setAttribute('aria-hidden', 'false');
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(atcHost);
  }

  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      const formId = atcHost.getAttribute('data-form-id');
      const form = formId ? document.getElementById(formId) : null;
      if (!form) return;

      const submitBtn = form.querySelector('[type="submit"], .product-form__cart-submit');
      if (submitBtn) submitBtn.click();
    });
  }
}

function initSection(section) {
  if (!section || section.getAttribute('data-abrsm-pdp-init') === 'true') return;
  section.setAttribute('data-abrsm-pdp-init', 'true');

  initClampToggles(section);
  initSocialProof(section);
  initSyllabusContext(section);
  initAtcBar(section);
  initMobileProductBlockOrder(section);
  initHoverSwap(section);
  initTabEmbeds(section);
  initTabKeyboard(section);
  initStickyAtc(section);
}

function initAll(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('.abrsm-pdp').forEach(initSection);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document));
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});
